import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { resolverUrl } from '../services/storageService';
import { 
  Container, 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Fade, 
  Grow,
  Zoom,
  Dialog, 
  DialogContent, 
  useTheme,
  IconButton,
  alpha,
  styled,
  Fab,
  Pagination,
  Tooltip,
  Button,
  DialogActions,
  DialogTitle
} from '@mui/material';
import { 
  Close as CloseIcon,
  ArrowBackIos as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { fetchImages, fetchProfileImages } from '../utils/api';

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '300px',
  
  '& .loading-text': {
    marginTop: '16px',
    color: theme.palette.text.secondary,
  }
}));

const GalleryHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2)
  }
}));

const GalleryTitle = styled(Typography)(({ theme }) => ({
  // El acento del tema no es para los titulos: pintaba de morado la unica
  // linea grande de la pantalla y competia con las fotos.
  fontWeight: 500,
  letterSpacing: '-0.01em',
  color: 'rgba(255, 255, 255, 0.92)',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem'
  }
}));

const ImageGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(2)
  },
  [theme.breakpoints.down('xs')]: {
    gridTemplateColumns: 'repeat(1, 1fr)',
  }
}));

const ImageContainer = styled(Box)(() => ({
  position: 'relative',
  borderRadius: 8,
  overflow: 'hidden',
  cursor: 'pointer',
  aspectRatio: '1/1',
  // El relleno era el morado del tema al 10%: teñia el hueco de cada foto
  // mientras cargaba, y con noventa y dos fotos eso pintaba media pantalla.
  // Sigue reservando el sitio, ahora en neutral.
  backgroundColor: 'rgba(255, 255, 255, 0.06)',

  // Sin sombra: sobre un lienzo casi negro una sombra no eleva nada, solo
  // ensucia el borde. La separacion la hace el espacio de la cuadricula.
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover .main-image': { opacity: 0.86 }
  },

  '& .placeholder-image': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    position: 'absolute',
    top: 0,
    left: 0,
    filter: 'blur(20px)',
    transform: 'scale(1.1)',
    opacity: 1,
    transition: 'opacity 0.4s ease',
    zIndex: 0
  },

  '& .main-image': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    // Sin brightness ni contrast: estas fotos son de quien las subio y
    // retocarlas desde la hoja de estilos es alterar el contenido.
    transition: 'opacity 200ms ease',
    position: 'relative',
    zIndex: 1,
    opacity: 0,
    '&.loaded': { opacity: 1 }
  },

  '@media (prefers-reduced-motion: reduce)': {
    '& .main-image, & .placeholder-image': { transition: 'none' }
  }
}));

const LightboxContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  cursor: 'pointer',
  userSelect: 'none',
  // Mejorar manejo táctil para dispositivos móviles
  touchAction: 'pan-y',
  WebkitTapHighlightColor: 'transparent',
  // Asegurar que ocupe todo el espacio disponible
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1)
  }
}));

const LightboxImage = styled('img')(({ theme }) => ({
  maxWidth: '90%',
  maxHeight: '90vh',
  objectFit: 'contain',
  margin: 'auto',
  display: 'block',
  borderRadius: '4px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  transition: 'transform 0.3s ease, opacity 0.3s ease',
}));

const LoadingIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.common.white,
  '& .MuiCircularProgress-root': {
    color: theme.palette.primary.main,
  },
  '& .loading-text': {
    marginTop: theme.spacing(2),
    fontSize: '0.9rem',
    opacity: 0.8,
  }
}));

const LightboxControls = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  left: 0,
  padding: theme.spacing(2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  zIndex: 10,
  opacity: 0,
  transition: 'opacity 0.3s ease-in-out',
  '&.visible': {
    opacity: 1
  },
  // Estilos específicos para dispositivos móviles
  [theme.breakpoints.down('sm')]: {
    opacity: 1, // Siempre visible en móviles
    padding: theme.spacing(1.5),
    '& .MuiIconButton-root': {
      width: '40px',
      height: '40px',
      backgroundColor: alpha(theme.palette.common.black, 0.6),
    }
  }
}));

const ImageCounter = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  backgroundColor: alpha(theme.palette.common.black, 0.5),
  padding: theme.spacing(0.5, 1.5),
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.875rem',
  fontWeight: 500,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    padding: theme.spacing(0.25, 1),
  }
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%) scale(0.9)',
  backgroundColor: alpha(theme.palette.background.paper, 0.3),
  color: theme.palette.common.white,
  opacity: 0,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    backgroundColor: alpha(theme.palette.background.paper, 0.6),
    transform: 'translateY(-50%) scale(1)'
  },
  '&.visible': {
    opacity: 1
  },
  // Estilos específicos para dispositivos móviles
  [theme.breakpoints.down('sm')]: {
    width: '40px',
    height: '40px',
    // Aumentar el área de toque para facilitar la interacción
    '& .MuiSvgIcon-root': {
      fontSize: '1.2rem',
    },
    // Asegurar que siempre sean visibles en móvil
    opacity: 1,
    backgroundColor: alpha(theme.palette.background.paper, 0.5),
  }
}));

// Sin caja ni centrado: una coleccion vacia no necesita un panel propio, y el
// texto centrado era lo unico centrado de la pagina.
const EmptyState = styled(Box)(() => ({
  padding: '48px 0',
  maxWidth: '46ch',

  '& h2': {
    marginBottom: '8px',
    fontSize: '1.125rem',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.92)'
  },

  '& p': {
    fontSize: '0.9375rem',
    color: 'rgba(255, 255, 255, 0.56)'
  }
}));

const ScrollTopButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  opacity: 0,
  visibility: 'hidden',
  transition: 'opacity 0.3s, visibility 0.3s, transform 0.3s',
  zIndex: 9,
  '&.visible': {
    opacity: 1,
    visibility: 'visible',
    transform: 'translateY(0)'
  },
  [theme.breakpoints.down('sm')]: {
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  }
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  '& .MuiPagination-ul': {
    justifyContent: 'center',
  }
}));

// URLs de las imágenes.
//
// Estas tres funciones generaban transformaciones de Cloudflare Images pegándole
// a https://cdn.gallerygg.site/cdn-cgi/image/... Eso ya no sirve por dos razones:
// el dominio dejó de pagarse y no resuelve, y /cdn-cgi/image/ solo funciona sobre
// un dominio dentro de tu zona de Cloudflare, no sobre workers.dev.
//
// Hoy las imágenes se sirven desde R2 a través de la API, que devuelve rutas
// relativas; resolverUrl() las vuelve absolutas.
//
// PENDIENTE DE OPTIMIZACIÓN: sin transformaciones, la grilla descarga cada foto
// en su tamaño original (~1,5 MB). Con 396 imágenes en un perfil eso es mucho
// tráfico. La solución es generar miniaturas en el Worker con el binding de
// Cloudflare Images (env.IMAGES.input(...).transform({width, height})), o
// precomputarlas al subir y guardarlas junto al original en el bucket.

const generateThumbnailUrl = (originalUrl) => resolverUrl(originalUrl);

const generatePlaceholderUrl = (originalUrl) => resolverUrl(originalUrl);

const getHighResUrl = (originalUrl) => resolverUrl(originalUrl);

const Gallery = ({ images: propImages, loading: propLoading, error: propError, profileId }) => {
  const [images, setImages] = useState(propImages || []);
  const [loading, setLoading] = useState(propLoading || true);
  const [error, setError] = useState(propError || null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isChangingImage, setIsChangingImage] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [loadedImages, setLoadedImages] = useState({});
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const lightboxRef = useRef(null);
  const controlsTimeout = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const galleryTitleRef = useRef(null);
  const theme = useTheme();
  // Usamos useAuth para obtener el rol del usuario
  const { user, userRole } = useAuth();
  
  // Cargar imágenes si no se proporcionan como prop
  useEffect(() => {
    const loadImages = async () => {
      if (propImages) {
        setImages(propImages);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Intentar cargar desde caché primero si no es un perfil específico
        let fetchedImages;
        const cacheKey = profileId ? `profile_images_${profileId}` : 'all_gallery_images';
        const cachedData = localStorage.getItem(cacheKey);
        const cachedTimestamp = localStorage.getItem(`${cacheKey}_timestamp`);
        const now = new Date().getTime();
        const cacheExpiry = 1000 * 60 * 30; // 30 minutos de caché
        
        // Usar caché si existe y no ha expirado
        if (cachedData && cachedTimestamp && (now - parseInt(cachedTimestamp)) < cacheExpiry) {
          console.log('Cargando imágenes desde caché');
          fetchedImages = JSON.parse(cachedData);
          setImages(fetchedImages);
          setLoading(false);
          
          // Actualizar en segundo plano
          refreshImagesInBackground(cacheKey);
        } else {
          // Cargar desde API
          if (profileId) {
            fetchedImages = await fetchProfileImages(profileId);
          } else {
            fetchedImages = await fetchImages();
          }
          
          // Guardar en caché
          localStorage.setItem(cacheKey, JSON.stringify(fetchedImages));
          localStorage.setItem(`${cacheKey}_timestamp`, now.toString());
          
          setImages(fetchedImages);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error loading images:', error);
        setError('Failed to load images. Please try again later.');
        setLoading(false);
      }
    };
    
    // Función para actualizar imágenes en segundo plano
    const refreshImagesInBackground = async (cacheKey) => {
      try {
        // Usar un timeout para no bloquear la interfaz de usuario
        setTimeout(async () => {
          let freshImages;
          if (profileId) {
            freshImages = await fetchProfileImages(profileId);
          } else {
            freshImages = await fetchImages();
          }
          
          // Actualizar caché con datos frescos
          localStorage.setItem(cacheKey, JSON.stringify(freshImages));
          localStorage.setItem(`${cacheKey}_timestamp`, new Date().getTime().toString());
          
          // Actualizar estado con los nuevos datos
          setImages(freshImages);
        }, 3000); // Esperar 3 segundos antes de actualizar en segundo plano
      } catch (error) {
        console.error('Error refreshing images in background:', error);
      }
    };
    
    loadImages();
  }, [profileId, propImages]);
  
  // Filtrar imágenes por perfil si se proporciona un profileId
  const displayImages = useMemo(() => {
    return images.filter(img => !profileId || img.profile_id === profileId);
  }, [images, profileId]);
  
  // Calcular el número total de páginas
  const totalPages = useMemo(() => {
    return Math.ceil(displayImages.length / itemsPerPage);
  }, [displayImages, itemsPerPage]);
  
  // Obtener las imágenes para la página actual
  const currentImages = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return displayImages.slice(startIndex, endIndex);
  }, [displayImages, page, itemsPerPage]);

  useEffect(() => {
    const handleScroll = () => {
      // Mostrar botón cuando se ha desplazado más de 300px
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  


  const handleImageClick = (image, index) => {
    setSelectedImage(image);
    setSelectedIndex(index);
    setLightboxOpen(true);
    setImageLoaded(false);
    setIsChangingImage(true);
    
    // Mostrar controles inicialmente y ocultarlos después de 2.5 segundos
    setControlsVisible(true);
    setTimeout(() => {
      setControlsVisible(false);
    }, 2500);
  };
  
  // Función para abrir el diálogo de confirmación de eliminación
  const handleDeleteClick = (event, image) => {
    event.stopPropagation(); // Evitar que se abra el lightbox
    setImageToDelete(image);
    setDeleteDialogOpen(true);
  };
  
  // Función para confirmar la eliminación de una imagen
  const confirmDelete = async () => {
    if (!imageToDelete) return;
    
    try {
      setDeleteLoading(true);
      
      // Llamar a la API para eliminar la imagen
      const { deleteImage } = await import('../utils/api');
      await deleteImage(imageToDelete.id);
      
      // Actualizar la lista de imágenes eliminando la imagen borrada
      setImages(prevImages => prevImages.filter(img => img.id !== imageToDelete.id));
      
      // Cerrar el diálogo
      setDeleteDialogOpen(false);
      setImageToDelete(null);
      
      // Mostrar mensaje de éxito (podrías implementar un sistema de notificaciones)
      console.log('Imagen eliminada correctamente');
    } catch (error) {
      console.error('Error al eliminar la imagen:', error);
      // Podrías mostrar un mensaje de error aquí
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCloseLightbox = useCallback(() => {
    // Cerrar directamente sin animación previa
    setLightboxOpen(false);
  }, []);

  const handlePrevImage = useCallback(() => {
    if (selectedIndex > 0) {
      setImageLoaded(false);
      setIsChangingImage(true);
      // Forzar visibilidad de controles al cambiar de imagen
      setControlsVisible(true);
      setSelectedIndex(selectedIndex - 1);
      setSelectedImage(displayImages[selectedIndex - 1]);
    }
  }, [selectedIndex, displayImages]);

  const handleNextImage = useCallback(() => {
    if (selectedIndex < displayImages.length - 1) {
      setIsChangingImage(true);
      setImageLoaded(false);
      // Forzar visibilidad de controles al cambiar de imagen
      setControlsVisible(true);
      setSelectedIndex(selectedIndex + 1);
      setSelectedImage(displayImages[selectedIndex + 1]);
    }
  }, [selectedIndex, displayImages]);

  // Manejador de movimiento del ratón
  const handleMouseMove = () => {
    // Mostrar controles al mover el ratón
    setControlsVisible(true);
    
    // Ocultar controles después de 3.5 segundos de inactividad en escritorio
    // y 5 segundos en dispositivos móviles
    clearTimeout(controlsTimeout.current);
    const timeoutDuration = window.innerWidth < 600 ? 5000 : 3500;
    
    controlsTimeout.current = setTimeout(() => {
      // En móviles, solo ocultamos los controles si no estamos cambiando de imagen
      if (window.innerWidth >= 600 || !isChangingImage) {
        setControlsVisible(false);
      }
    }, timeoutDuration);
  };
  
  // Manejadores de eventos táctiles para navegación por deslizamiento
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    // Mostrar controles al iniciar toque
    setControlsVisible(true);
  };
  
  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diffX = touchStartX.current - touchEndX.current;
    
    // Si el deslizamiento es significativo (más de 50px)
    if (Math.abs(diffX) > 50) {
      if (diffX > 0) {
        // Deslizamiento hacia la izquierda -> siguiente imagen
        handleNextImage();
      } else {
        // Deslizamiento hacia la derecha -> imagen anterior
        handlePrevImage();
      }
    } else {
      // Si es un toque simple (no deslizamiento), alternar visibilidad de controles
      setControlsVisible(!controlsVisible);
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    setIsChangingImage(false);
  };
  
  // Manejar la carga de imágenes en miniaturas
  const handleThumbnailLoad = (imageId) => {
    setLoadedImages(prev => ({
      ...prev,
      [imageId]: true
    }));
  };

  const handleKeyDown = useCallback((e) => {
    if (!lightboxOpen) return;
    
    switch (e.key) {
      case 'ArrowLeft':
        handlePrevImage();
        break;
      case 'ArrowRight':
        handleNextImage();
        break;
      case 'Escape':
        handleCloseLightbox();
        break;
      default:
        break;
    }
  }, [lightboxOpen, handlePrevImage, handleNextImage, handleCloseLightbox]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearTimeout(controlsTimeout.current);
    };
  }, [handleKeyDown]);

  // Manejar cambio de página
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    // Desplazar hasta el título de la galería en lugar de hasta el inicio
    if (galleryTitleRef.current) {
      galleryTitleRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <StyledBox>
        <CircularProgress size={50} />
        <Typography className="loading-text" variant="h6">
          Loading images...
        </Typography>
      </StyledBox>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        {error}
      </Alert>
    );
  }

  if (!displayImages || displayImages.length === 0) {
    return (
      <EmptyState>
        <Typography variant="h2">No images yet</Typography>
        <Typography variant="body1">
          {profileId 
            ? "This collection doesn't have any images yet. Upload some to get started!"
            : "There are no images in the gallery yet. Upload some to get started!"}
        </Typography>
      </EmptyState>
    );
  }

  return (
    // Sin Container propio: ya vive dentro del de ProfileView y los padding se
    // sumaban. Sin Fade de 800ms tampoco: retrasaba ver las fotos.
    <Box ref={galleryTitleRef}>
      {/* La cabecera solo cuando la galeria se usa suelta. Dentro de una
          coleccion repetia el nombre y el conteo que ya estan arriba, y su
          titulo era un segundo <h1> en la misma pagina. */}
      {!profileId && (
        <GalleryHeader>
          <GalleryTitle variant="h4" component="h1">
            All Images
          </GalleryTitle>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.56)' }}>
            {displayImages.length} {displayImages.length === 1 ? 'image' : 'images'}
          </Typography>
        </GalleryHeader>
      )}

        {loading ? (
          <StyledBox>
            <CircularProgress size={60} thickness={4} />
            <Typography variant="body1" className="loading-text">
              Loading images...
            </Typography>
          </StyledBox>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : displayImages.length === 0 ? (
          <EmptyState>
            <Typography variant="h5" component="h2">
              No images found
            </Typography>
            <Typography variant="body1">
              {profileId 
                ? 'This collection doesn\'t have any images yet.' 
                : 'There are no images in the gallery yet.'}
            </Typography>
          </EmptyState>
        ) : (
          <>
            <ImageGrid>
              {currentImages.map((image, index) => (
                // Sin el Grow escalonado: con veinte fotos por pagina la ultima
                // aparecia a los cuatro segundos. El blur-up de cada imagen ya
                // avisa que algo esta cargando.
                <React.Fragment key={image.id}>
                  <ImageContainer onClick={() => handleImageClick(image, (page - 1) * itemsPerPage + index)}>
                    {/* Imagen de baja resolución para efecto blur-up */}
                    {!imageErrors[image.id] && (
                      <img 
                        className="placeholder-image"
                        src={generatePlaceholderUrl(image.url)}
                        alt=""
                        aria-hidden="true"
                      />
                    )}
                    
                    {/* Imagen principal con resolución normal */}
                    <img 
                      className={`main-image ${loadedImages[image.id] ? 'loaded' : ''}`}
                      src={imageErrors[image.id] 
                        ? 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iIzFmMWYxZiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMThweCIgZmlsbD0iI2JiODZmYyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSI+SW1hZ2UgTm90IEF2YWlsYWJsZTwvdGV4dD48L3N2Zz4=' 
                        : generateThumbnailUrl(image.url, window.innerWidth < 600 ? 150 : 250)}
                      alt={image.title || 'Gallery image'}
                      onLoad={() => handleThumbnailLoad(image.id)}
                      onError={() => {
                        setImageErrors(prev => ({
                          ...prev,
                          [image.id]: true
                        }));
                      }}
                    />
                    
                    {/* Botón de eliminación solo para administradores */}
                    {userRole === 'admin' && (
                      <Tooltip title="Eliminar imagen">
                        <IconButton
                          size="small"
                          color="error"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            '&:hover': {
                              backgroundColor: 'rgba(0,0,0,0.7)',
                            },
                            zIndex: 10
                          }}
                          onClick={(e) => handleDeleteClick(e, image)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </ImageContainer>
                </React.Fragment>
              ))}
            </ImageGrid>
            
            {totalPages > 1 && (
              <PaginationContainer>
                <Pagination 
                  count={totalPages} 
                  page={page} 
                  onChange={handlePageChange} 
                  color="primary"
                  size={window.innerWidth < 600 ? "small" : "medium"}
                  showFirstButton
                  showLastButton
                />
              </PaginationContainer>
            )}

            <ScrollTopButton 
              color="primary" 
              size="small"
              onClick={scrollToTop}
              className={showScrollTop ? 'visible' : ''}
              aria-label="scroll to top"
            >
              <KeyboardArrowUpIcon />
            </ScrollTopButton>
            
            {/* Diálogo de confirmación para eliminar imagen */}
            <Dialog
              open={deleteDialogOpen}
              onClose={() => setDeleteDialogOpen(false)}
              aria-labelledby="delete-dialog-title"
              aria-describedby="delete-dialog-description"
            >
              <DialogTitle id="delete-dialog-title">
                ¿Eliminar imagen?
              </DialogTitle>
              <DialogContent>
                <Typography variant="body1">
                  Esta acción no se puede deshacer. ¿Estás seguro de que deseas eliminar esta imagen?
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button 
                  onClick={() => setDeleteDialogOpen(false)} 
                  color="primary"
                  disabled={deleteLoading}
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={confirmDelete} 
                  color="error" 
                  variant="contained"
                  disabled={deleteLoading}
                  startIcon={deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
                >
                  {deleteLoading ? 'Eliminando...' : 'Eliminar'}
                </Button>
              </DialogActions>
            </Dialog>
            
            <Dialog 
              open={lightboxOpen} 
              onClose={handleCloseLightbox}
              maxWidth={false}
              fullScreen
              TransitionComponent={Zoom}
              TransitionProps={{
                timeout: 300
              }}
              PaperProps={{
                style: {
                  backgroundColor: 'rgba(0, 0, 0, 0.95)',
                  boxShadow: 'none',
                  margin: 0,
                  borderRadius: 0,
                  width: '100%',
                  height: '100%'
                }
              }}
            >
              <LightboxContainer 
                ref={lightboxRef}
                onMouseMove={handleMouseMove}
                onClick={() => setControlsVisible(!controlsVisible)}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                <LightboxControls className={controlsVisible ? 'visible' : ''}>
                  {/* Contador de imágenes - muestra la posición actual */}
                  <ImageCounter>
                    {selectedIndex + 1} / {displayImages.length}
                  </ImageCounter>
                  
                  {/* Botón de cierre */}
                  <IconButton 
                    onClick={handleCloseLightbox}
                    sx={{ 
                      color: 'white',
                      bgcolor: alpha(theme.palette.common.black, 0.5),
                      '&:hover': {
                        bgcolor: alpha(theme.palette.common.black, 0.7),
                      },
                      // Estilos específicos para dispositivos móviles
                      [theme.breakpoints.down('sm')]: {
                        width: '48px',
                        height: '48px',
                        bgcolor: alpha(theme.palette.common.black, 0.7),
                        border: '2px solid rgba(255,255,255,0.3)',
                        '& .MuiSvgIcon-root': {
                          fontSize: '1.5rem',
                        }
                      }
                    }}
                    aria-label="Cerrar vista de imagen"
                  >
                    <CloseIcon />
                  </IconButton>
                </LightboxControls>

                {/* Botón de navegación a imagen anterior - siempre visible en móvil */}
                <NavigationButton 
                  onClick={handlePrevImage}
                  className={controlsVisible ? 'visible' : ''}
                  sx={{ 
                    left: theme.spacing(2),
                    // Asegurar que sea visible en móviles incluso si es la primera imagen
                    [theme.breakpoints.down('sm')]: {
                      display: selectedIndex > 0 ? 'flex' : 'none',
                      opacity: 1,
                      zIndex: 20
                    }
                  }}
                  disabled={selectedIndex === 0}
                  aria-label="Imagen anterior"
                >
                  <ArrowBackIcon />
                </NavigationButton>

                {selectedImage && (
                  <>
                    {/* Indicador de carga */}
                    {isChangingImage && (
                      <LoadingIndicator>
                        <CircularProgress size={40} thickness={4} />
                        <Typography className="loading-text">
                          Cargando imagen...
                        </Typography>
                      </LoadingIndicator>
                    )}
                    
                    {/* Imagen de baja resolución para efecto blur-up */}
                    <LightboxImage
                      className="lightbox-placeholder"
                      src={generatePlaceholderUrl(selectedImage.url)}
                      alt=""
                      aria-hidden="true"
                      style={{
                        filter: 'blur(30px) brightness(1.1)',
                        transform: 'scale(1.1)',
                        position: 'absolute',
                        opacity: imageLoaded ? 0 : 1,
                        transition: 'opacity 0.5s ease-out',
                      }}
                    />
                    
                    {/* Imagen de alta resolución */}
                    <Fade in={true} timeout={300}>
                      <LightboxImage
                        className="lightbox-image"
                        src={getHighResUrl(selectedImage.url)}
                        alt={selectedImage.title || 'Gallery image'}
                        onLoad={handleImageLoad}
                        style={{
                          opacity: imageLoaded ? 1 : 0,
                          transform: imageLoaded ? 'scale(1)' : 'scale(0.95)',
                          transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
                        }}
                      />
                    </Fade>
                  </>
                )}

                {/* Botón de navegación a siguiente imagen - siempre visible en móvil */}
                <NavigationButton 
                  onClick={handleNextImage}
                  className={controlsVisible ? 'visible' : ''}
                  sx={{ 
                    right: theme.spacing(2),
                    // Asegurar que sea visible en móviles incluso si es la última imagen
                    [theme.breakpoints.down('sm')]: {
                      display: selectedIndex < displayImages.length - 1 ? 'flex' : 'none',
                      opacity: 1,
                      zIndex: 20
                    }
                  }}
                  disabled={selectedIndex === displayImages.length - 1}
                  aria-label="Siguiente imagen"
                >
                  <ArrowForwardIcon />
                </NavigationButton>
              </LightboxContainer>
            </Dialog>
          </>
        )}
    </Box>
  );
};

// Función para comparar props y evitar re-renderizados innecesarios
const areEqual = (prevProps, nextProps) => {
  // Si el profileId cambia, debemos re-renderizar
  if (prevProps.profileId !== nextProps.profileId) return false;
  
  // Si las imágenes proporcionadas como props cambian en cantidad, debemos re-renderizar
  if (prevProps.images?.length !== nextProps.images?.length) return false;
  
  // Si el estado de carga o error cambia, debemos re-renderizar
  if (prevProps.loading !== nextProps.loading || prevProps.error !== nextProps.error) return false;
  
  // En otros casos, podemos evitar el re-renderizado
  return true;
};

// Exportamos el componente envuelto en React.memo para optimizar renderizados
export default React.memo(Gallery, areEqual);
